import React, { useState, useEffect, useMemo } from 'react';
import './AnalysisGrid.css';
import api from '../api/axiosConfig';
import DualScrollTable from '../components/DualScrollTable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ko } from 'date-fns/locale';
import { format } from 'date-fns';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { startOfMonth, endOfMonth } from 'date-fns';

const AnalysisGrid = () => {
  const [rows, setRows] = useState([]);
  const [data, setData] = useState([]);

  const today = new Date();
  const [startDate, setStartDate] = useState(startOfMonth(today));
  const [endDate, setEndDate] = useState(endOfMonth(today));
  const [fieldId, setFieldId] = useState('');
  const [fieldOptions, setFieldOptions] = useState([]);

  const [rowsWithSubtotal, setRowsWithSubtotal] = useState([]);
  const [totalRow, setTotalRow] = useState({});

  // 현장 조회
  useEffect(() => {
    api
      .get(`/api/fieldList`)
      .then((res) => {
        const uniqueOptions = res.data.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.FIELD_ID === item.FIELD_ID)
        );
        setFieldOptions(uniqueOptions);
      })
      .catch((err) => console.error(err));
  }, []);

    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerId, setCustomerId] = useState('');

     useEffect(() => {
         api
             .get(`/api/customer/customerList`)
             .then(res => {
                 const uniq = (res.data || []).filter(
                     (it, idx, arr) => idx === arr.findIndex(t => t.CUSTOMER_ID === it.CUSTOMER_ID)
                 );
                 setCustomerOptions(uniq);
             })
             .catch(err => console.error("거래처 옵션 로드 실패:", err));
     }, []);


  // 세금계산서 현황 조회
  useEffect(() => {
    const start = format(startDate, 'yyyy-MM-dd');
    const end = format(endDate, 'yyyy-MM-dd');
    api
      .get(
        `/api/analysis/searchList?startDate=${start}&endDate=${end}&fieldId=${fieldId || ''}&customerId=${customerId || ''}`
      )
      .then((res) => {
        setData(res.data);
        setRows(res.data);
      })
      .catch((err) => console.error(err));
  }, [startDate, endDate, fieldId, customerId]);

  const formatNumber = (num) => {
    if (num === null || num === undefined || num === '') return '';
    const number = parseFloat(num.toString().replace(/,/g, ''));
    return isNaN(number) ? '' : number.toLocaleString();
  };

  // 소계/총계 계산
  const calculateSubtotals = (list) => {
    const result = [];
    let currentField = '';
    let subtotal = {
      OUT_PROVIDE_AMOUNT: 0,
      OUT_VAT: 0,
      OUT_TOTAL_AMOUNT: 0,
      UNPAID_AMOUNT_OUT: 0,
      IN_PROVIDE_AMOUNT: 0,
      IN_VAT: 0,
      IN_TOTAL_AMOUNT: 0,
      IN_PAID_AMOUNT: 0,
      REMAIN_AMOUNT: 0,
      MATERIAL_PROVIDE_AMOUNT: 0,
      MATERIAL_VAT: 0,
      MATERIAL_TOTAL_AMOUNT: 0,
      EQUIP_PROVIDE_AMOUNT: 0,
      EQUIP_VAT: 0,
      EQUIP_TOTAL_AMOUNT: 0,
    };

    let total = { ...subtotal };

    const pushSubtotalRow = () => {
      result.push({
        FIELD_NAME: `소계`,
        ISSUED_DATE: '',
        CUSTOMER_NAME: '',
        ...subtotal,
        isSubtotal: true,
      });
    };

    list.forEach((row, idx) => {
      if (row.FIELD_NAME !== currentField && currentField !== '') {
        pushSubtotalRow();
        subtotal = Object.fromEntries(Object.keys(subtotal).map((k) => [k, 0]));
      }

      currentField = row.FIELD_NAME;
      result.push(row);

      for (const key in subtotal) {
        const val = parseFloat(row[key]) || 0;
        subtotal[key] += val;
        total[key] += val;
      }
    });

    if (currentField !== '') {
      pushSubtotalRow();
    }

    setTotalRow({
      FIELD_NAME: '총계',
      ...total,
      isTotal: true,
    });

    return result;
  };

  useEffect(() => {
    setRowsWithSubtotal(calculateSubtotals(rows));
  }, [rows]);

  // =========================
  // 인쇄
  // =========================
  const handlePrint = () => {
    const printContents = document.getElementById('print-area').innerHTML;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  // =========================
  // 엑셀 내보내기
  // =========================
  const exportToExcel = async () => {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('세금계산서 현황');

      // --- 타이틀 및 헤더 생성 ---
      const formattedStartDate = format(startDate, 'yyyy.MM.dd');
      const formattedEndDate = format(endDate, 'yyyy.MM.dd');
      const title = `${formattedStartDate} ~ ${formattedEndDate} 매출, 매입 현황`;

      worksheet.mergeCells('A1', 'P1');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = title;
      titleCell.font = { bold: true, size: 14 };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9C4' } };

      const groupHeader = [
          '', '', '', '', '매출', '', '', '', '매입', '', '', '', '', '', '', '',
      ];
      const headerRow1 = worksheet.addRow(groupHeader);
      headerRow1.eachCell((cell) => {
          if (cell.value) {
              cell.alignment = { horizontal: 'center', vertical: 'middle' };
              cell.font = { bold: true };
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E0E0E0' } };
          }
      });
      worksheet.mergeCells('E2:H2');
      worksheet.mergeCells('I2:M2');

      const headers = [
          'No', '현장명', '발행일자', '거래처',
          '공급가액', '세액', '합계', '미결금',
          '공급가액', '세액', '합계', '결제금액', '잔금',
          '비고', '정산일', '계좌번호',
      ];
      const headerRow2 = worksheet.addRow(headers);
      headerRow2.font = { bold: true };
      headerRow2.alignment = { horizontal: 'center', vertical: 'middle' };

      // --- 데이터 추가 ---
      let no = 1;
      const fieldRowMap = []; // ✅ 병합을 위한 행 추적

      rowsWithSubtotal.forEach((row) => {
          const isSubtotal = row.isSubtotal;
          const excelRow = [
              isSubtotal ? '' : no,  // ✅ no++ 제거 (병합 후 값 지정)
              row.FIELD_NAME, row.ISSUED_DATE, row.CUSTOMER_NAME,
              row.OUT_PROVIDE_AMOUNT, row.OUT_VAT, row.OUT_TOTAL_AMOUNT, row.UNPAID_AMOUNT_OUT,
              row.IN_PROVIDE_AMOUNT, row.IN_VAT, row.IN_TOTAL_AMOUNT, row.IN_PAID_AMOUNT, row.REMAIN_AMOUNT,
              row.REMARK, row.PAID_DATE, row.BANK_ACCOUNT
          ];
          const addedRow = worksheet.addRow(excelRow);

          // 빈 셀 강제 초기화 (테두리 적용을 위해)
          for (let col = 1; col <= 16; col++) {
              const cell = addedRow.getCell(col);
              if (cell.value === undefined || cell.value === null) {
                  cell.value = '';
              }
          }

          // ✅ 데이터 행만 병합 추적 대상에 추가
          if (!isSubtotal) {
              fieldRowMap.push({
                  rowNum: addedRow.number,
                  fieldName: row.FIELD_NAME,
                  no: no,
              });
              no++;
          }

          if (isSubtotal) {
              addedRow.eachCell((cell) => {
                  cell.font = { bold: true };
                  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFDE7' } };
              });
          }
      });

      // --- 총계 추가 ---
      const totalData = [
          '', '총계', '', '',
          totalRow.OUT_PROVIDE_AMOUNT, totalRow.OUT_VAT, totalRow.OUT_TOTAL_AMOUNT, totalRow.UNPAID_AMOUNT_OUT,
          totalRow.IN_PROVIDE_AMOUNT, totalRow.IN_VAT, totalRow.IN_TOTAL_AMOUNT, totalRow.IN_PAID_AMOUNT, totalRow.REMAIN_AMOUNT,
          '', '', '',
      ];
      const totalExcelRow = worksheet.addRow(totalData);

      for (let col = 1; col <= 16; col++) {
          const cell = totalExcelRow.getCell(col);
          if (cell.value === undefined || cell.value === null) cell.value = '';
      }

      totalExcelRow.eachCell((cell) => {
          cell.font = { bold: true };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'C8E6C9' } };
      });

      // ======================================================
      // ✅ NO, 현장명 병합 처리
      // 소계 행을 건너뛰고 연속된 같은 현장명끼리만 병합
      // ======================================================
      let i = 0;
      while (i < fieldRowMap.length) {
          const current = fieldRowMap[i];
          let j = i + 1;

          // 같은 현장명이 연속되는 구간 탐색
          while (j < fieldRowMap.length && fieldRowMap[j].fieldName === current.fieldName) {
              j++;
          }

          const sameCount = j - i;

          if (sameCount > 1) {
              const startRow = current.rowNum;
              const endRow = fieldRowMap[j - 1].rowNum;

              // NO 열(1) 병합
              worksheet.mergeCells(startRow, 1, endRow, 1);
              const noCell = worksheet.getCell(startRow, 1);
              noCell.value = current.no;
              noCell.alignment = { horizontal: 'center', vertical: 'middle' };

              // 현장명 열(2) 병합
              worksheet.mergeCells(startRow, 2, endRow, 2);
              const nameCell = worksheet.getCell(startRow, 2);
              nameCell.value = current.fieldName;
              nameCell.alignment = { horizontal: 'left', vertical: 'middle' };
          }

          i = j;
      }

      // ======================================================
      // 컬럼 너비, 정렬, 테두리 적용
      // ======================================================
      const centerAlignedColumns = [1, 3, 15, 17];
      const rightAlignedColumns = [
          5, 6, 7, 8,
          9, 10, 11, 12, 13,
      ];

      worksheet.getColumn(1).width = 6;
      worksheet.getColumn(2).width = 25;
      worksheet.getColumn(3).width = 12;
      worksheet.getColumn(4).width = 28;
      worksheet.getColumn(5).width = 15;
      worksheet.getColumn(6).width = 15;
      worksheet.getColumn(7).width = 15;
      worksheet.getColumn(8).width = 15;
      worksheet.getColumn(9).width = 15;
      worksheet.getColumn(10).width = 15;
      worksheet.getColumn(11).width = 15;
      worksheet.getColumn(12).width = 15;
      worksheet.getColumn(13).width = 15;
      worksheet.getColumn(14).width = 25;
      worksheet.getColumn(15).width = 12;
      worksheet.getColumn(16).width = 32;

      worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
          row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
              // 테두리 적용
              cell.border = {
                  top: { style: 'thin' }, left: { style: 'thin' },
                  bottom: { style: 'thin' }, right: { style: 'thin' }
              };

              // 가운데 정렬 (헤더 행 제외)
              if (rowNumber > 2 && centerAlignedColumns.includes(colNumber)) {
                  cell.alignment = { horizontal: 'center', vertical: 'middle' };
              }
          });

          // 숫자 열 우측 정렬 (헤더 행 제외)
          if (rowNumber > 3) {
              rightAlignedColumns.forEach(colIndex => {
                  const cell = row.getCell(colIndex);
                  cell.alignment = { horizontal: 'right', vertical: 'middle' };
                  cell.numFmt = '#,##0';
              });
          }
      });

      // --- 파일 저장 ---
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      saveAs(blob, `매출매입_현황_${format(new Date(), 'yyyyMMdd_HHmm')}.xlsx`);
  };

  return (
    <div className="grid-container">
      {/* 조회조건 바 */}
      <h2>기간별 매출, 매입 내역 (*기타경비 제외) </h2>

      <div className="gh-band">
        <div className="gh-band__row">
          <div className="gh-band__title">
            <span className="gh-octo" aria-hidden>▶</span>
            <span>조회조건</span>
          </div>
        </div>
        <div className="gh-band__filters">
          <div className="filter-item gh-field">
            <label>발행일자</label>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center', // 내부 요소 중앙 정렬
                gap: '0', // 갭을 0으로 하고 내부에서 조절
                border: '1px solid #d0d7de',
                borderRadius: '4px',
                padding: '0 4px',
                height: '32px',
                backgroundColor: '#fff',
                width: '230px', // 너비를 약간 줄여서 더 콤팩트하게 설정
                overflow: 'hidden',
                flexShrink: 0 // 부모에 의해 찌그러지지 않도록 보호
              }}>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                dateFormat="yyyy-MM-dd"
                locale={ko}
                placeholderText="시작일"
                wrapperStyle={{ width: '105px', flexShrink: 0 }} // wrapper 너비 고정
                customInput={
                  <input style={{
                    width: '105px',
                    border: 'none', // 테두리 제거 필수
                    outline: 'none',
                    fontSize: '12px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: 'transparent',
                    padding: 0
                  }} />
                }
              />

              <span style={{ color: '#d0d7de', fontSize: '12px', flexShrink: 0 }}>~</span>

              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                dateFormat="yyyy-MM-dd"
                locale={ko}
                placeholderText="종료일"
                wrapperStyle={{ width: '105px', flexShrink: 0 }}
                customInput={
                  <input style={{
                    width: '105px',
                    border: 'none', // 테두리 제거 필수
                    outline: 'none',
                    fontSize: '12px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: 'transparent',
                    padding: 0
                  }} />
                }
              />
            </div>
          </div>
          <div className="filter-item gh-field">
            <label >현장</label>
            <Autocomplete
              options={fieldOptions}
              value={fieldOptions.find((option) => option.FIELD_ID === fieldId) || null}
              onChange={(event, newValue) => {
                setFieldId(newValue ? newValue.FIELD_ID : '');
              }}
              getOptionLabel={(option) => option?.FIELD_NAME || ''}
              isOptionEqualToValue={(option, value) => option.FIELD_ID === value.FIELD_ID}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="현장 선택"
                  variant="standard"
                  InputProps={{ ...params.InputProps, disableUnderline: true }}
                  sx={{ px: 1, minWidth: 200 }}
                />
              )}
            />
          </div>

          <div className="filter-item gh-field">
            <label>거래처</label>
            <Autocomplete
              options={customerOptions}
              value={customerOptions.find((option) => option.CUSTOMER_ID === customerId) || null}
              onChange={(event, newValue) => {
                setCustomerId(newValue ? newValue.CUSTOMER_ID : '');
              }}
              getOptionLabel={(option) => option?.CUSTOMER_NAME || ''}
              isOptionEqualToValue={(option, value) => option.CUSTOMER_ID === value.CUSTOMER_ID}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="거래처 선택"
                  variant="standard"
                  InputProps={{ ...params.InputProps, disableUnderline: true }}
                  sx={{ px: 1, minWidth: 200 }}
                />
              )}
            />
          </div>


          <div className="filter-actions gh-actions">
            <button className="btn"
              onClick={() => {
                if (!startDate || !endDate) {
                  alert('조회 기간을 모두 선택해주세요.');
                  return;
                }
                const start = format(startDate, 'yyyy-MM-dd');
                const end = format(endDate, 'yyyy-MM-dd');
                api
                  .get(
                    `/api/analysis/searchList?startDate=${start}&endDate=${end}&fieldId=${fieldId}&customerId=${customerId}`
                  )
                  .then((res) => setRows(res.data))
                  .catch((err) => console.error(err));
              }}>조회</button>
          </div>
        </div>
      </div>

      {/* 버튼 그룹 */}
      <div className="button-group-right">
        <button onClick={exportToExcel} className="btn btn-primary">
          엑셀 내려받기
        </button>
        <button onClick={handlePrint} className="btn btn-ghost">
          출력하기
        </button>
      </div>

      {/* 표 */}
      <div className="grid-table-wrapper">
        <div id="print-area">
          <table className="grid-table">
            <thead>
              <tr>
                <th style={{ width: '55px' }}></th>
                <th></th>
                <th style={{ width: '100px' }}></th>
                <th></th>
                <th colSpan="4">매출</th>
                <th colSpan="5" className="header-purchase">
                  매입
                </th>
                <th></th>
                <th></th>
                <th></th>
                <th className="col-print-hide"></th>
                <th className="col-print-hide" colSpan="3">장비대</th>
                <th className="col-print-hide" colSpan="3">자재대</th>
              </tr>
              <tr>
                <th style={{ width: '55px' }}>No</th>
                <th>현장명</th>
                <th style={{ width: '100px' }}>발행일자</th>
                <th>거래처</th>
                <th>공급가액</th>
                <th>세액</th>
                <th>합계</th>
                <th>미결금</th>
                <th>공급가액</th>
                <th>세액</th>
                <th>합계</th>
                <th>결제금액</th>
                <th>잔금</th>
                <th>정산일</th>
                <th className="col-remark">비고</th>
                <th>계좌번호</th>
                  <th className="col-print-hide">장비</th>
                  <th className="col-print-hide">공급가</th>
                  <th className="col-print-hide">부가세</th>
                  <th className="col-print-hide">합계금액</th>
                  <th className="col-print-hide">공급가</th>
                  <th className="col-print-hide">부가세</th>
                  <th className="col-print-hide">합계금액</th>
              </tr>
            </thead>

            <tbody>
              {(() => {
                let noCounter = 1;
                const result = [];

                for (let idx = 0; idx < rowsWithSubtotal.length; idx++) {
                  const row = rowsWithSubtotal[idx];
                  const isSubtotal = row.isSubtotal;
                  const prevRow = rowsWithSubtotal[idx - 1];
                  const nextRow = rowsWithSubtotal[idx + 1];

                  const isFirstOfGroup =
                    !isSubtotal &&
                    (idx === 0 || row.FIELD_NAME !== prevRow?.FIELD_NAME);

                  // rowSpan 계산
                  let rowSpan = 1;
                  if (isFirstOfGroup) {
                    rowSpan = 0;
                    for (let i = idx; i < rowsWithSubtotal.length; i++) {
                      if (
                        rowsWithSubtotal[i].FIELD_NAME !== row.FIELD_NAME ||
                        rowsWithSubtotal[i].isSubtotal
                      )
                        break;
                      rowSpan++;
                    }
                  }

                  result.push(
                    <tr key={idx} className={isSubtotal ? 'subtotal-row' : ''}>
                      {/* No 병합 */}
                      {isFirstOfGroup && !isSubtotal ? (
                        <td rowSpan={rowSpan}>{noCounter++}</td>
                      ) : !isSubtotal ? null : (
                        <td></td>
                      )}

                      {/* 현장명 병합 */}
                      {isFirstOfGroup && !isSubtotal ? (
                        <td rowSpan={rowSpan}>
                          <span style={{ textAlign: 'left' }}>{row.FIELD_NAME}</span>
                        </td>
                      ) : !isSubtotal ? null : (
                        <td>
                          <span>{row.FIELD_NAME}</span>
                        </td>
                      )}

                      <td>
                        <span>{row.ISSUED_DATE}</span>
                      </td>
                      <td>
                        <span style={{ textAlign: 'left' }}>{row.CUSTOMER_NAME}</span>
                      </td>

                      {/* 매출 */}
                      <td>
                        <span className="right-align">
                          {formatNumber(row.OUT_PROVIDE_AMOUNT)}
                        </span>
                      </td>
                      <td>
                        <span className="right-align">{formatNumber(row.OUT_VAT)}</span>
                      </td>
                      <td>
                        <span className="right-align">
                          {formatNumber(row.OUT_TOTAL_AMOUNT)}
                        </span>
                      </td>
                      <td
                        style={
                          !row.isSubtotal && parseFloat(row.UNPAID_AMOUNT_OUT) > 0
                            ? { backgroundColor: '#fff8c5' }
                            : {}
                        }
                      >
                        <span className="right-align">
                          {formatNumber(row.UNPAID_AMOUNT_OUT)}
                        </span>
                      </td>

                      {/* 매입 */}
                      <td>
                        <span className="right-align">
                          {formatNumber(row.IN_PROVIDE_AMOUNT)}
                        </span>
                      </td>
                      <td>
                        <span className="right-align">{formatNumber(row.IN_VAT)}</span>
                      </td>
                      <td>
                        <span className="right-align">
                          {formatNumber(row.IN_TOTAL_AMOUNT)}
                        </span>
                      </td>
                      <td>
                        <span className="right-align">
                          {formatNumber(row.IN_PAID_AMOUNT)}
                        </span>
                      </td>
                      <td
                        style={
                          !row.isSubtotal && parseFloat(row.REMAIN_AMOUNT) > 0
                            ? { backgroundColor: '#fff8c5' }
                            : {}
                        }
                      >
                        <span className="right-align">
                          {formatNumber(row.REMAIN_AMOUNT)}
                        </span>
                      </td>
                      <td>
                        <span>{row.PAID_DATE}</span>
                      </td>
                        <td className="col-remark">
                          <span style={{
                            textAlign: 'left',
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            display: 'block',
                            maxWidth: '200px'
                          }}>
                            {row.REMARK}
                          </span>
                        </td>
                      {/* 계좌번호: 좌측정렬 */}
                      <td className="account-cell">
                        <span>{row.BANK_ACCOUNT}</span>
                      </td>

                        <td className="col-print-hide">
                          <span>{row.DIVISION}</span>
                        </td>
                        <td className="col-print-hide">
                          <span className="right-align">{formatNumber(row.EQUIP_PROVIDE_AMOUNT)}</span>
                        </td>
                        <td className="col-print-hide">
                          <span className="right-align">{formatNumber(row.EQUIP_VAT)}</span>
                        </td>
                        <td className="col-print-hide">
                          <span className="right-align">{formatNumber(row.EQUIP_TOTAL_AMOUNT)}</span>
                        </td>
                        <td className="col-print-hide">
                          <span className="right-align">{formatNumber(row.MATERIAL_PROVIDE_AMOUNT)}</span>
                        </td>
                        <td className="col-print-hide">
                          <span className="right-align">{formatNumber(row.MATERIAL_VAT)}</span>
                        </td>
                        <td className="col-print-hide">
                          <span className="right-align">{formatNumber(row.MATERIAL_TOTAL_AMOUNT)}</span>
                        </td>

                    </tr>
                  );
                }

                // 총계 행
                result.push(
                  <tr key="total" className="total-row">
                    <td></td>
                    <td>
                      <strong>총계</strong>
                    </td>
                    <td></td>
                    <td></td>
                    <td>
                      <span className="right-align">
                        {formatNumber(totalRow.OUT_PROVIDE_AMOUNT)}
                      </span>
                    </td>
                    <td>
                      <span className="right-align">
                        {formatNumber(totalRow.OUT_VAT)}
                      </span>
                    </td>
                    <td>
                      <span className="right-align">
                        {formatNumber(totalRow.OUT_TOTAL_AMOUNT)}
                      </span>
                    </td>
                    <td>
                      <span className="right-align">
                        {formatNumber(totalRow.UNPAID_AMOUNT_OUT)}
                      </span>
                    </td>
                    <td>
                      <span className="right-align">
                        {formatNumber(totalRow.IN_PROVIDE_AMOUNT)}
                      </span>
                    </td>
                    <td>
                      <span className="right-align">
                        {formatNumber(totalRow.IN_VAT)}
                      </span>
                    </td>
                    <td>
                      <span className="right-align">
                        {formatNumber(totalRow.IN_TOTAL_AMOUNT)}
                      </span>
                    </td>
                    <td>
                      <span className="right-align">
                        {formatNumber(totalRow.IN_PAID_AMOUNT)}
                      </span>
                    </td>
                    <td>
                      <span className="right-align">
                        {formatNumber(totalRow.REMAIN_AMOUNT)}
                      </span>
                    </td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td className="col-print-hide"></td>
                    <td className="col-print-hide">
                      <span className="right-align">
                        {formatNumber(totalRow.EQUIP_PROVIDE_AMOUNT)}
                      </span>
                    </td>
                    <td className="col-print-hide">
                      <span className="right-align">
                        {formatNumber(totalRow.EQUIP_VAT)}
                      </span>
                    </td>
                    <td className="col-print-hide">
                      <span className="right-align">
                        {formatNumber(totalRow.EQUIP_TOTAL_AMOUNT)}
                      </span>
                    </td>
                    <td className="col-print-hide">
                      <span className="right-align">
                        {formatNumber(totalRow.MATERIAL_PROVIDE_AMOUNT)}
                      </span>
                    </td>
                    <td className="col-print-hide">
                      <span className="right-align">
                        {formatNumber(totalRow.MATERIAL_VAT)}
                      </span>
                    </td>
                    <td className="col-print-hide">
                      <span className="right-align">
                        {formatNumber(totalRow.MATERIAL_TOTAL_AMOUNT)}
                      </span>
                    </td>
                  </tr>
                );

                return result;
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AnalysisGrid;